import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, BadgeCheck, ChevronRight } from 'lucide-react';

import { loadReportAnalytics } from './api';
import { tx } from './i18n';
import {
  CHART, ChartTip, HeadFacts, KpiRow, NUMBER, Panel, RangeTabs, Share, counted, hourly, lengthOf, percent, pointLabel,
  stackRows, tipPoint, versusAverage,
} from './MobileParts';
import { Empty, Failed, Head, Loading, WorkspaceTabs } from './parts';
import {
  SOURCES, SourceMark, bytesLabel, durationLabel, reasonLabel, sourceLabel, statusLabel,
} from './ReportParts';
import { useLiveResource } from './useLive';

// What the reports in a range add up to: how many, from which service, of which
// type and language, how long they took, why some failed, and how quickly
// clinicians validated collection results. The server counts; this only draws.

const text = (key, values) => tx(`ui.reports.analytics.${key}`, values);
const SHADES = [CHART.deep, CHART.ink, CHART.mid, CHART.soft, CHART.pale];
const TOP = 4;

// The parts one chart stacks, for the dimension picked: fixed for source and
// status, the largest few for type and language with the rest together.
function chartParts(data, by) {
  if (by === 'source') {
    return SOURCES.filter((code) => code !== 'unknown' || data.sources.some((row) => row.code === 'unknown' && row.reports > 0))
      .map((code, index) => ({ key: code, name: sourceLabel(code), color: SHADES[index], pick: (point) => point.bySource[code] || 0 }));
  }
  if (by === 'status') {
    return [
      { key: 'done', name: statusLabel('done'), color: CHART.ink, pick: (point) => point.byStatus.done || 0 },
      { key: 'failed', name: statusLabel('failed'), color: CHART.failed, pick: (point) => point.byStatus.failed || 0 },
      { key: 'active', name: text('in_progress'), color: CHART.soft,
        pick: (point) => (point.byStatus.queued || 0) + (point.byStatus.processing || 0) },
      { key: 'cancelled', name: statusLabel('cancelled'), color: CHART.pale, pick: (point) => point.byStatus.cancelled || 0 },
    ];
  }
  const rows = by === 'type' ? data.reportTypes : data.languages;
  const keyOf = (row) => String(by === 'type' ? row.id : row.code);
  const field = by === 'type' ? 'byReportType' : 'byLanguage';
  const top = rows.slice(0, TOP);
  const parts = top.map((row, index) => ({
    key: `k${keyOf(row)}`, name: row.name, color: SHADES[index], pick: (point) => point[field][keyOf(row)] || 0,
  }));
  if (rows.length > TOP) {
    const shown = new Set(top.map(keyOf));
    parts.push({ key: 'rest', name: text('everything_else'), color: SHADES[TOP],
      pick: (point) => Object.entries(point[field]).filter(([key]) => !shown.has(key)).reduce((sum, [, value]) => sum + value, 0) });
  }
  return parts;
}

function VolumeChart({ data }) {
  const [by, setBy] = useState('source');
  const parts = useMemo(() => chartParts(data, by), [data, by]);
  const points = useMemo(() => data.series.map((point) => ({
    at: point.at, total: point.reports, ...Object.fromEntries(parts.map((part) => [part.key, part.pick(point)])),
  })), [data.series, parts]);
  const average = data.totals.reports / Math.max(1, data.series.length);
  return (
    <>
      <WorkspaceTabs value={by} onChange={setBy} label={text('volume_by')} className="is-compact xo-mob-places-tabs" tabs={[
        { value: 'source', label: text('by_source') },
        { value: 'status', label: text('by_status') },
        { value: 'type', label: text('by_type') },
        { value: 'language', label: text('by_language') },
      ]} />
      {data.totals.reports === 0 ? <Empty title={text('no_reports')} detail={text('no_reports_detail')} /> : (
        <div className="xo-mob-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} margin={{ top: 6, right: 14, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={CHART.grid} />
              <XAxis dataKey="at" tickFormatter={pointLabel(data.step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={32} />
              <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                <ChartTip title={tipPoint(data.step)} rows={(point) => stackRows(point, parts)}
                  facts={(point) => [
                    { label: tx('ui.mobile.tip_share_of_range'), value: point.total > 0 ? percent(point.total, data.totals.reports) : null },
                    point.total > 0 ? versusAverage(point.total, average) : { note: text('quiet') },
                  ]} />
              )} />
              {parts.map((part, index) => (
                <Bar key={part.key} dataKey={part.key} stackId="reports" fill={part.color} maxBarSize={22} name={part.name}
                  radius={index === parts.length - 1 ? [3, 3, 0, 0] : undefined} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="xo-mob-key">
        {parts.map((part) => <span key={part.key}><i style={{ background: part.color }} />{part.name}</span>)}
      </div>
    </>
  );
}

function Sources({ data }) {
  const most = Math.max(1, ...data.sources.map((row) => row.reports));
  return (
    <ol className="xo-mob-rank xo-rep-rank">
      {data.sources.filter((row) => row.code !== 'unknown' || row.reports > 0).map((row) => (
        <li key={row.code}>
          <span className="xo-mob-rank-icon"><SourceMark source={row.code} compact /></span>
          <span className="xo-mob-rank-main">
            <Link to={`/xomics/reporting/files?source=${row.code}`}><strong>{sourceLabel(row.code)}</strong></Link>
            <small>{text(`source_detail_${row.code}`)}</small>
          </span>
          <Share value={row.reports} total={most} />
          <em>{NUMBER.format(row.reports)}</em>
        </li>
      ))}
    </ol>
  );
}

function bucketLabel(bucket, index, count) {
  if (index === 0) return text('duration_under', { upTo: durationLabel(bucket.upToMs) });
  if (index === count - 1) return text('duration_over', { from: durationLabel(bucket.fromMs) });
  return text('duration_between', { from: durationLabel(bucket.fromMs), upTo: durationLabel(bucket.upToMs) });
}

// The distribution as ranked rows rather than a chart: six bands whose names are
// sentences read at any width, where an axis of them would collide on a phone.
function Durations({ data }) {
  const buckets = data.duration.buckets;
  const most = Math.max(1, ...buckets.map((bucket) => bucket.reports));
  return (
    <>
      <HeadFacts items={[
        { label: text('median'), value: durationLabel(data.totals.medianGenerationMs) },
        { label: text('p90'), value: durationLabel(data.totals.p90GenerationMs) },
        { label: text('median_wait'), value: durationLabel(data.totals.medianWaitMs) },
      ]} />
      {data.duration.reports === 0 ? <Empty title={text('no_durations')} /> : (
        <ol className="xo-mob-rank xo-rep-buckets">
          {buckets.map((bucket, index) => (
            <li key={bucket.fromMs} className={bucket.reports === 0 ? 'is-empty' : ''}>
              <span className="xo-mob-rank-main">
                <strong>{bucketLabel(bucket, index, buckets.length)}</strong>
                <small>{text('bucket_share', { share: percent(bucket.reports, data.duration.reports) })}</small>
              </span>
              <Share value={bucket.reports} total={most} />
              <em>{NUMBER.format(bucket.reports)}</em>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

// How the range's reports ended, as one bar: ready, failed and still being made.
function Outcomes({ totals }) {
  const parts = [
    { key: 'done', label: statusLabel('done'), value: totals.done },
    { key: 'failed', label: statusLabel('failed'), value: totals.failed },
    { key: 'active', label: text('outcome_active'), value: totals.active },
  ].filter((part) => part.value > 0);
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  if (total === 0) return null;
  return (
    <div className="xo-rep-outcomes">
      <div className="xo-rep-outcome-bar" role="img" aria-label={parts.map((part) => `${part.label} ${percent(part.value, total)}`).join(', ')}>
        {parts.map((part) => <i key={part.key} className={`is-${part.key}`} style={{ flexGrow: part.value }} />)}
      </div>
      <ul className="xo-rep-outcome-key">
        {parts.map((part) => (
          <li key={part.key}><i className={`is-${part.key}`} /><span>{part.label}</span><b>{NUMBER.format(part.value)}</b><em>{percent(part.value, total)}</em></li>
        ))}
      </ul>
    </div>
  );
}

function Failures({ data }) {
  if (data.failures.total === 0) return <Empty title={text('no_failures')} detail={text('no_failures_detail')} />;
  const most = Math.max(1, ...data.failures.reasons.map((row) => row.reports));
  return (
    <ol className="xo-mob-rank xo-rep-rank">
      {data.failures.reasons.map((row) => (
        <li key={row.code}>
          <span className="xo-mob-rank-icon is-bad"><AlertTriangle aria-hidden="true" /></span>
          <span className="xo-mob-rank-main">
            <Link to={`/xomics/reporting/files?reason=${row.code}`}><strong>{reasonLabel(row.code)}</strong></Link>
            <small>{text(`reason_detail_${row.code}`)}</small>
          </span>
          <Share value={row.reports} total={most} />
          <em>{NUMBER.format(row.reports)}</em>
        </li>
      ))}
    </ol>
  );
}

const DIMENSIONS = [
  { value: 'type', rows: 'reportTypes', filter: 'reportType', id: (row) => row.id },
  { value: 'language', rows: 'languages', filter: 'language', id: (row) => row.code },
  { value: 'pipeline', rows: 'pipelines', filter: 'pipeline', id: (row) => row.id, unassigned: 'pipeline' },
  { value: 'product', rows: 'products', filter: 'product', id: (row) => row.id, unassigned: 'product' },
  { value: 'project', rows: 'projects', filter: 'project', id: (row) => row.id, unassigned: 'project' },
  { value: 'organization', rows: 'organizations', filter: 'organization', id: (row) => row.id, unassigned: 'organization' },
  { value: 'company', rows: 'companies', filter: 'company', id: (row) => row.id },
];

function Breakdown({ data }) {
  const [by, setBy] = useState('type');
  const dimension = DIMENSIONS.find((entry) => entry.value === by);
  const rows = data[dimension.rows];
  const most = Math.max(1, ...rows.map((row) => row.reports));
  const unassigned = dimension.unassigned ? data.unassigned[dimension.unassigned] : 0;
  return (
    <>
      <WorkspaceTabs value={by} onChange={setBy} label={text('breakdown_by')} scrollable className="is-compact xo-mob-places-tabs"
        tabs={DIMENSIONS.map((entry) => ({ value: entry.value, label: text(`dimension_${entry.value}`), count: data[entry.rows].length }))} />
      {rows.length === 0 ? <Empty title={text(`no_${dimension.value}`)} detail={text('no_links_detail')} /> : (
        <ol className="xo-mob-rank xo-rep-breakdown">
          {rows.map((row) => (
            <li key={String(dimension.id(row))}>
              <span className="xo-mob-rank-main">
                <Link to={`/xomics/reporting/files?${dimension.filter}=${encodeURIComponent(dimension.id(row))}`}>
                  <strong>{row.name}{row.codeVersion ? ` ${row.codeVersion}` : ''}</strong>
                </Link>
                <small>{text('breakdown_detail', {
                  done: NUMBER.format(row.done), failed: NUMBER.format(row.failed),
                  median: durationLabel(row.medianGenerationMs), size: bytesLabel(row.bytes),
                })}</small>
              </span>
              <Share value={row.reports} total={most} />
              <em>{counted('ui.reports.analytics.reports_count', row.reports)}</em>
            </li>
          ))}
        </ol>
      )}
      {unassigned > 0 && <p className="xo-mob-footnote">{counted(`ui.reports.analytics.unassigned_${dimension.value}`, unassigned)}</p>}
    </>
  );
}

function Validation({ data }) {
  const validation = data.validation;
  if (validation.decided === 0 && validation.pending === 0) {
    return <Empty title={text('no_validations')} detail={text('no_validations_detail')} />;
  }
  return (
    <div className="xo-rep-validation">
      <HeadFacts items={[
        { label: text('validation_approved'), value: NUMBER.format(validation.approved) },
        { label: text('validation_rejected'), value: NUMBER.format(validation.rejected) },
        { label: text('validation_pending'), value: NUMBER.format(validation.pending) },
      ]} />
      <dl className="xo-rep-validation-times">
        <div><dt>{text('turnaround_median')}</dt><dd>{validation.medianTurnaroundMs === null ? text('not_yet') : lengthOf(validation.medianTurnaroundMs)}</dd></div>
        <div><dt>{text('turnaround_p90')}</dt><dd>{validation.p90TurnaroundMs === null ? text('not_yet') : lengthOf(validation.p90TurnaroundMs)}</dd></div>
        <div><dt>{text('oldest_pending')}</dt><dd>{validation.oldestPendingMs === null ? text('nothing_waiting') : lengthOf(validation.oldestPendingMs)}</dd></div>
      </dl>
      <p className="xo-mob-footnote"><BadgeCheck aria-hidden="true" />{text('validation_footnote')}</p>
    </div>
  );
}

export default function ReportAnalytics() {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadReportAnalytics({ days }), [days]);
  const analytics = useLiveResource(['reports', 'validations'], load, { reconcileMs: 60000 });

  const data = analytics.data;
  const totals = data?.totals;
  return (
    <div className="xo-mobile-page xo-rep-page">
      <Head title={text('title')} subtitle={text('subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {analytics.loading && data === null && <Loading label={text('loading')} />}
      {analytics.error && data === null && <Failed message={analytics.error} onRetry={analytics.refresh} />}
      {data && (
        <>
          {analytics.error && <p className="xo-alert t-warn" role="alert">{analytics.error}</p>}
          <KpiRow items={[
            { label: text('kpi_reports'), value: NUMBER.format(totals.reports),
              detail: text('kpi_reports_detail', { done: NUMBER.format(totals.done), failed: NUMBER.format(totals.failed), active: NUMBER.format(totals.active) }),
              trend: data.series.map((point) => point.reports) },
            { label: text('kpi_generation'), value: durationLabel(totals.medianGenerationMs),
              detail: text('kpi_generation_detail', { p90: durationLabel(totals.p90GenerationMs) }) },
            { label: text('kpi_failures'), value: totals.failureShare === null ? text('not_yet') : percent(totals.failed, totals.done + totals.failed),
              detail: counted('ui.reports.analytics.kpi_failures_detail', totals.failed) },
            { label: text('kpi_turnaround'), value: data.validation.medianTurnaroundMs === null ? text('not_yet') : lengthOf(data.validation.medianTurnaroundMs),
              detail: counted('ui.reports.analytics.kpi_turnaround_detail', data.validation.pending) },
            { label: text('kpi_stored'), value: bytesLabel(totals.bytes),
              detail: counted('ui.reports.analytics.kpi_stored_detail', totals.patients) },
          ]} />

          <div className="xo-mob-row is-wide-left is-even">
            <Panel title={text('volume_title')}
              detail={text(hourly(data.step) ? 'volume_detail_hours' : 'volume_detail', { average: NUMBER.format(Math.round((totals.reports / Math.max(1, data.series.length)) * 10) / 10) })}>
              <VolumeChart data={data} />
            </Panel>
            <Panel title={text('sources_title')} detail={text('sources_detail')}
              aside={<Link className="xo-mob-link" to="/xomics/reporting/files">{text('open_library')}<ChevronRight aria-hidden="true" /></Link>}>
              <Sources data={data} />
            </Panel>
          </div>

          <div className="xo-mob-row is-halves is-even">
            <Panel title={text('duration_title')} detail={text('duration_detail')}>
              <Durations data={data} />
            </Panel>
            <Panel title={text('failures_title')} detail={text('failures_detail')}>
              <Outcomes totals={totals} />
              <Failures data={data} />
            </Panel>
          </div>

          <div className="xo-mob-row is-wide-left is-even">
            <Panel title={text('breakdown_title')} detail={text('breakdown_detail_heading')}>
              <Breakdown data={data} />
            </Panel>
            <Panel title={text('validation_title')} detail={text('validation_detail')}>
              <Validation data={data} />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
